import { useCallback, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import Humanize from 'humanize-plus';
import { Stack, Typography, Tooltip } from '@mui/material';
import { useParams } from 'react-router-dom';
import { ReactComponent as InfoIcon } from '../../../assets/info-icon.svg';
import { ReactComponent as DownloadIcon } from '../../../assets/download.svg';
import {
  Button,
  Flex,
  FlexCenter,
  Breadcrumbs,
  FlexColumn,
  TableFilters,
  QuickFilter,
  TableEmptyState,
  ToggleColumnsMenu,
  DisplayPanel,
  TestResultsTable,
  type TestResultsTableData,
} from '../../../components';
import { getValue, isInSaaSMode } from '../../../utils/common';
import { localizeDate } from '../../../utils/datetime';
import { useBreadcrumbs } from '../../../hooks/useBreadcrumbs';
import useNavigateWithEnvParam from '../../../hooks/useNavigateWithEnvParam';
import useSelectedTreeItem from '../../../hooks/useSelectedTreeItem';
import useSearchParams from '../../../hooks/useSearchParams';
import useViewLoadTracking from '../../../hooks/useViewLoadTracking';
import { TEST_RESULTS_TABLE_FILTERS } from '../../../constants/urlParams';
import { TestResultsProps } from '../types';
import { fontSize, radius, space } from '../../../style/utils';
import { getTestResultsTableColumns } from './columns';

const InvocationWrapper = styled(Flex)`
  background: ${({ theme }) => theme.palette.surface.subtle};
  padding: ${space('sm', 'lg')};
  border-radius: ${radius('sm')};
`;

const InvocationText = styled(Typography)`
  font-size: ${fontSize('sm')};
  color: ${({ theme }) => theme.palette.content.secondary};
`;

const TableWrapper = styled(FlexColumn)`
  position: relative;
  background-color: ${({ theme }) => theme.palette.surface.base};
  overflow-y: hidden;
  width: 100%;
  height: 100%;
  gap: ${space('xxl')};
`;

const TableHeader = styled(Flex)`
  gap: ${space('xxl')};
  padding-bottom: ${space('lg')};
  width: 100%;
  justify-content: space-between;
  align-items: flex-start;
`;

const TestResults = ({
  tableData,
  filterOptions,
  isFetching,
  isFilterOptionsFetching,
  fetchNextPage,
  hasNextPage,
  daysBack,
  invocationData,
  search,
  onSearch,
  expandedRowRenderer,
  groupBy,
  onGroupByChange,
  groupableColumnIds,
  groupedData,
  expandedGroups,
  onGroupToggle,
  onGroupScrollToBottom,
  onCSVDownload,
  isExporting,
}: TestResultsProps) => {
  const isSaaS = isInSaaSMode();
  const isGrouped = !!groupedData;
  const { selectedTreeNode } = useSelectedTreeItem();

  const breadcrumbs = useBreadcrumbs({
    pageTitle: 'Tests',
  });

  const { id: selectedTestId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigateWithEnvParam();

  const onScrollToBottom = useCallback(() => {
    if (fetchNextPage && hasNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage]);

  const totals = useMemo(() => {
    const totalsObj = {
      errors: 0,
      failures: 0,
      warnings: 0,
      passed: 0,
      all: tableData.length,
    };

    if (filterOptions) {
      totalsObj.all = 0;
      const statusFilterOptions = filterOptions['latestRunStatus'] || [];
      statusFilterOptions.forEach((option) => {
        totalsObj.all += option.count || 0;
        if (option.value.toLowerCase() === 'error') {
          totalsObj.errors += option.count || 0;
        } else if (option.value.toLowerCase() === 'fail') {
          totalsObj.failures += option.count || 0;
        } else if (option.value.toLowerCase() === 'warn') {
          totalsObj.warnings += option.count || 0;
        } else if (option.value.toLowerCase() === 'pass') {
          totalsObj.passed += option.count || 0;
        }
      });

      return totalsObj;
    }

    tableData.forEach((testResult) => {
      const lastRunStatus = testResult.latestRunStatus;
      totalsObj.errors += lastRunStatus === 'error' ? 1 : 0;
      totalsObj.failures += lastRunStatus === 'fail' ? 1 : 0;
      totalsObj.warnings += lastRunStatus === 'warn' ? 1 : 0;
      totalsObj.passed += lastRunStatus === 'pass' ? 1 : 0;
    });

    return totalsObj;
  }, [tableData, filterOptions]);

  const hasResults = isGrouped
    ? groupedData.some((group) => (group.count ?? group.data.length) > 0)
    : tableData.length > 0;

  const columns = getTestResultsTableColumns(tableData, filterOptions);

  const filteredColumns = useMemo(
    () =>
      selectedTreeNode &&
      !['test', 'folder'].includes(selectedTreeNode?.resourceType || '')
        ? columns.filter((c) => c.id !== 'tableName')
        : columns,
    [columns, selectedTreeNode],
  );

  const invocation = useMemo(() => {
    if (!invocationData || !invocationData?.invocation_id) {
      return '';
    }

    const formattedTimestamp = localizeDate(invocationData.detected_at).format(
      'L LTS',
    );

    if (
      !(
        invocationData.command ||
        invocationData.selected ||
        invocationData.full_refresh
      )
    )
      return formattedTimestamp;

    return `${formattedTimestamp} | ${
      invocationData.command ? `dbt ${invocationData.command}` : ''
    } ${invocationData.selected ? `-s ${invocationData.selected}` : ''} ${
      invocationData.full_refresh ? '--full-refresh' : ''
    }`;
  }, [invocationData]);

  const quickFilters: QuickFilter[] = [
    {
      name: `Passed (${Humanize.intComma(totals.passed)})`,
      filters: [
        {
          property: 'latestRunStatus',
          operator: 'equals',
          value: ['pass'],
        },
      ],
    },
    {
      name: `Warnings (${Humanize.intComma(totals.warnings)})`,
      filters: [
        {
          property: 'latestRunStatus',
          operator: 'equals',
          value: ['warn'],
        },
      ],
    },
    {
      name: `Errors (${Humanize.intComma(totals.failures + totals.errors)})`,
      filters: [
        {
          property: 'latestRunStatus',
          operator: 'equals',
          value: ['fail', 'error'],
        },
      ],
    },
  ].filter(Boolean) as QuickFilter[];

  const tableSearchFunction = useCallback(
    (searchVal: string, row: TestResultsTableData) => {
      const searchColumns: (keyof TestResultsTableData)[] = [
        'tableName',
        'columnName',
        'testName',
        'testSubType',
      ];
      return searchColumns.some((col) =>
        getValue(row, col)
          ?.toString()
          .toLowerCase()
          .includes(searchVal.toLowerCase()),
      );
    },
    [],
  );

  useEffect(() => {
    if (selectedTestId && !isFetching) {
      const isSelectedTestExists = tableData.some(
        (test) => test.subTestUniqueId === selectedTestId,
      );

      if (!isSelectedTestExists) {
        navigate(`/report/test-results?${searchParams.toString()}`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTestId, tableData, isFetching]);

  useViewLoadTracking({
    name: 'Test results page',
    loadDeps: [(tableData?.length && !isFetching) || null],
  });

  return (
    <Stack sx={{ width: '100%', height: '100%', p: 3, position: 'relative' }}>
      <Stack sx={{ mb: 3, gap: 3, width: '100%' }}>
        <Stack
          direction="row"
          sx={{ justifyContent: 'space-between', alignItems: 'center' }}
        >
          <Breadcrumbs items={breadcrumbs} />
          <InvocationWrapper>
            {invocation ? (
              <FlexCenter sx={{ gap: 1 }}>
                <InvocationText>Invocation</InvocationText>
                <Tooltip title={invocation}>
                  <Flex>
                    <InfoIcon />
                  </Flex>
                </Tooltip>
              </FlexCenter>
            ) : (
              <InvocationText>Last {daysBack} Days</InvocationText>
            )}
          </InvocationWrapper>
        </Stack>
      </Stack>

      <TableWrapper>
        <TableHeader>
          <Flex gap={space('xxl')}>
            <TableFilters
              data={tableData}
              columns={filteredColumns}
              urlParamsPrefix={TEST_RESULTS_TABLE_FILTERS}
              search={search}
              onSearch={onSearch}
              quickFilters={quickFilters}
              clearFiltersChipLabel={`All (${Humanize.intComma(totals.all)})`}
              disabled={isFetching}
            />
          </Flex>
          <Flex sx={{ gap: 1 }}>
            {onCSVDownload && (
              <Button
                variant="outlined"
                onClick={onCSVDownload}
                startIcon={<DownloadIcon width={12} />}
                disabled={!hasResults || isExporting}
              >
                {isExporting ? 'Exporting...' : 'CSV'}
              </Button>
            )}
            {isSaaS ? (
              <DisplayPanel
                columns={filteredColumns}
                localStorageKey="test_results_table"
                groupBy={groupBy}
                onGroupByChange={onGroupByChange}
                groupableColumnIds={groupableColumnIds}
              />
            ) : (
              <ToggleColumnsMenu
                columns={filteredColumns}
                localStorageKey="test_results_table"
              />
            )}
          </Flex>
        </TableHeader>
        <TestResultsTable
          data={tableData}
          columns={filteredColumns}
          scrollItemId={selectedTestId}
          search={search}
          tableSearchFunction={
            fetchNextPage || isGrouped ? undefined : tableSearchFunction
          }
          emptyStateRenderer={TableEmptyState}
          expandedRowRenderer={expandedRowRenderer}
          isLoading={isFetching}
          isFilterOptionsLoading={isFilterOptionsFetching}
          onScrollToBottom={onScrollToBottom}
          isControlled={!!fetchNextPage || isGrouped}
          bottomScrollPadding={fetchNextPage || isGrouped ? 100 : undefined}
          groupedData={groupedData}
          expandedGroups={expandedGroups}
          onGroupToggle={onGroupToggle}
          onGroupScrollToBottom={onGroupScrollToBottom}
        />
      </TableWrapper>
    </Stack>
  );
};

export default TestResults;
